// Custom relay client — multiplexes Nostr NIP-01 + P2P protocol
// on the same WebSocket connection.
import { CHAT_KIND } from './dm.js'

// VITE_RELAY_URL can be set at build time to inject the self-hosted relay as the
// first default. Falls back to public Nostr relays that support NIP-01 for item
// discovery; P2P signaling will be silently ignored by those relays.
const _envRelay = import.meta.env?.VITE_RELAY_URL
const DEFAULT_RELAYS = _envRelay
  ? [_envRelay, 'wss://relay.damus.io', 'wss://nos.lol', 'wss://relay.snort.social']
  : ['wss://relay.damus.io', 'wss://nos.lol', 'wss://relay.snort.social', 'wss://purplepag.es']

// --- Single relay connection ---
class saysheepRelay {
  constructor(url, onP2PMessage) {
    this.url = url
    this.ws = null
    this.subs = new Map()      // subId -> {filters, onEvent, onEose}
    this.pendingOks = new Map() // eventId -> {resolve, reject, timer}
    this.onP2PMessage = onP2PMessage
    this.connected = false
    this.nextReconnectAt = null
    this._reconnectDelay = 1000
    this._dead = false
    // Capability info from a "proper" saysheep relay's CAP handshake (e.g. its
    // OG preview base). Stays null for generic Nostr relays, which ignore CAP.
    this.capability = null
    this._connect()
  }

  _connect() {
    if (this._dead) return
    this.nextReconnectAt = null
    try {
      this.ws = new WebSocket(this.url)
    } catch {
      this._scheduleReconnect()
      return
    }

    this.ws.onopen = () => {
      this.connected = true
      this.nextReconnectAt = null
      this._reconnectDelay = 1000
      // Re-send active subscriptions
      for (const [subId, sub] of this.subs) {
        this.ws.send(JSON.stringify(['REQ', subId, ...sub.filters]))
      }
      // Probe for saysheep-relay capabilities (generic relays ignore this).
      this.ws.send(JSON.stringify(['CAP']))
      this.onP2PMessage?.({ type: '_connected', relay: this })
    }

    this.ws.onmessage = ({ data }) => {
      let msg
      try { msg = JSON.parse(data) } catch { return }
      if (!Array.isArray(msg)) return

      if (msg[0] === 'P2P') {
        this.onP2PMessage?.(msg)
        return
      }

      if (msg[0] === 'CAP') {
        this.capability = msg[1] || {}
        return
      }

      const [type, a, b] = msg
      if (type === 'EVENT') {
        this.subs.get(a)?.onEvent(b)
      } else if (type === 'EOSE') {
        this.subs.get(a)?.onEose?.()
      } else if (type === 'OK') {
        const [, id, ok, reason] = msg
        const p = this.pendingOks.get(id)
        if (p) {
          clearTimeout(p.timer)
          ok ? p.resolve() : p.reject(reason || 'rejected')
          this.pendingOks.delete(id)
        }
      }
    }

    this.ws.onclose = () => {
      this.connected = false
      this.onP2PMessage?.({ type: '_disconnected', relay: this })
      this._scheduleReconnect()
    }
    this.ws.onerror = () => {}
  }

  _scheduleReconnect() {
    if (this._dead) return
    this.nextReconnectAt = Date.now() + this._reconnectDelay
    setTimeout(() => this._connect(), this._reconnectDelay)
    this._reconnectDelay = Math.min(this._reconnectDelay * 2, 30000)
    this.onP2PMessage?.({ type: '_reconnect_scheduled', relay: this })
  }

  subscribe(subId, filters, onEvent, onEose) {
    this.subs.set(subId, { filters, onEvent, onEose })
    if (this.connected) this.ws.send(JSON.stringify(['REQ', subId, ...filters]))
    return () => this.unsubscribe(subId)
  }

  unsubscribe(subId) {
    if (this.subs.has(subId)) {
      this.subs.delete(subId)
      if (this.connected) this.ws.send(JSON.stringify(['CLOSE', subId]))
    }
  }

  publish(event) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingOks.delete(event.id)
        resolve() // timeout = optimistically ok
      }, 5000)
      this.pendingOks.set(event.id, { resolve, reject, timer })
      if (this.connected) {
        this.ws.send(JSON.stringify(['EVENT', event]))
      } else {
        reject('not connected')
      }
    })
  }

  // Send a raw P2P control message
  sendP2P(msg) {
    if (this.connected) this.ws.send(JSON.stringify(msg))
  }

  close() {
    this._dead = true
    this.ws?.close()
  }
}

// --- Multi-relay pool ---
let _relays = []
let _connections = new Map() // url -> saysheepRelay
let _p2pHandler = null
let _onCountChange = null

const _emitCount = () => _onCountChange?.(getRelayCount())

export const initRelay = (relayUrls, onP2PMessage, onCountChange) => {
  _p2pHandler = onP2PMessage
  _onCountChange = onCountChange
  _relays = relayUrls?.length ? relayUrls : getStoredRelays()
  for (const url of _relays) {
    if (!_connections.has(url)) {
      _connections.set(url, new saysheepRelay(url, _routeP2P))
    }
  }
}

const _routeP2P = (msg) => {
  if (msg?.type === '_connected' || msg?.type === '_disconnected' || msg?.type === '_reconnect_scheduled') _emitCount()
  _p2pHandler?.(msg)
}

export const getRelaysStatus = () => {
  return [..._connections.entries()].map(([url, conn]) => ({
    url,
    connected: conn.connected,
    nextReconnectAt: conn.nextReconnectAt,
    saysheep: !!conn.capability, // answered the CAP handshake
  }))
}

// The OG preview base of any currently-connected "proper" saysheep relay (the
// first one found), or null if only generic relays are connected. Used at share
// time to build a rich-preview link without hard-coding any domain.
export const getOgBase = () => {
  for (const conn of _connections.values()) {
    if (conn.connected && conn.capability?.og_base) return conn.capability.og_base.replace(/\/$/, '')
  }
  return null
}

export const getRelays = () => _relays

export const addRelay = (url) => {
  if (_relays.includes(url)) return
  _relays.push(url)
  saveRelays(_relays)
  _connections.set(url, new saysheepRelay(url, _routeP2P))
}

export const removeRelay = (url) => {
  _relays = _relays.filter(r => r !== url)
  saveRelays(_relays)
  _connections.get(url)?.close()
  _connections.delete(url)
}

export const getRelayCount = () =>
  [..._connections.values()].filter(r => r.connected).length

const getStoredRelays = () => {
  try {
    const s = localStorage.getItem('saysheep_relays')
    const p = s ? JSON.parse(s) : null
    return p?.length ? p : [...DEFAULT_RELAYS]
  } catch { return [...DEFAULT_RELAYS] }
}

const saveRelays = (relays) =>
  localStorage.setItem('saysheep_relays', JSON.stringify(relays))

export const publishEvent = async (event) => {
  const results = await Promise.allSettled(
    [..._connections.values()].map(r => r.publish(event))
  )
  return results
}

export const subscribeArea = (geohashPrefixes, onEvent, onEose) => {
  const subId = 'area-' + Math.random().toString(36).slice(2)
  const since = Math.floor(Date.now() / 1000) - 14 * 86400
  const filters = [
    { kinds: [30402], '#g': geohashPrefixes, since, limit: 200 },
    { kinds: [30403], since, limit: 200 }
  ]
  const unsubs = []
  for (const conn of _connections.values()) {
    unsubs.push(conn.subscribe(subId, filters, onEvent, onEose))
  }
  return () => unsubs.forEach(fn => fn())
}

// Subscribe to private item-chat (NIP-44) DMs addressed to me OR sent by me, so
// every device reconstructs the same threads. Content stays encrypted; only the
// recipient can read it.
export const subscribeDMs = (myPubkey, onEvent) => {
  const subId = 'dm-' + Math.random().toString(36).slice(2)
  const filters = [
    { kinds: [CHAT_KIND], '#p': [myPubkey], limit: 500 },
    { kinds: [CHAT_KIND], authors: [myPubkey], limit: 500 },
  ]
  const unsubs = []
  for (const conn of _connections.values()) {
    unsubs.push(conn.subscribe(subId, filters, onEvent, null))
  }
  return () => unsubs.forEach(fn => fn())
}

// Send a P2P control message to the first available relay
// (relay routes it via its DHT to the target)
export const sendP2P = (msg) => {
  for (const conn of _connections.values()) {
    if (conn.connected) { conn.sendP2P(msg); return }
  }
}
